import { ParamOrganizationSlug, ParamProjectSlugOrAll, ParamRegionUrl } from "./schema-DKEjeVqP.js";
import { defineTool } from "./define-SD6AQa4b.js";
import { apiServiceFromContext } from "./api-EaEPyKlQ.js";
import { structuredResult } from "./results-NF1RJbDZ.js";
import { assertProjectRefWithinConstraint } from "./project-constraints-DlfcU4mm.js";
import { z } from "zod";
import { setTag } from "@sentry/core";

//#region src/tools/catalog/find-monitors.ts
const ENVIRONMENT_LIMIT = 5;
const monitorScheduleOutputSchema = z.object({
	type: z.string().nullable(),
	value: z.string().nullable(),
	checkInMargin: z.number().nullable(),
	maxRuntime: z.number().nullable()
});
const monitorEnvironmentOutputSchema = z.object({
	name: z.string().nullable(),
	status: z.string().nullable(),
	lastCheckIn: z.string().datetime().nullable()
});
const monitorSummaryOutputSchema = z.object({
	id: z.string().nullable(),
	slug: z.string(),
	name: z.string(),
	projectSlug: z.string().nullable(),
	status: z.string().nullable(),
	owner: z.string().nullable(),
	lastCheckIn: z.string().datetime().nullable(),
	nextCheckIn: z.string().datetime().nullable(),
	webUrl: z.string().url(),
	schedule: monitorScheduleOutputSchema.nullable(),
	environments: z.array(monitorEnvironmentOutputSchema),
	hasMoreEnvironments: z.boolean()
});
const findMonitorsOutputSchema = z.object({
	monitors: z.array(monitorSummaryOutputSchema),
	hasMore: z.boolean()
});
function getOwnerName(monitor) {
	const owner = monitor.owner;
	if (!owner) return null;
	if (typeof owner === "string") return owner.trim() || null;
	return owner.name?.trim() || owner.email?.trim() || owner.username?.trim() || (owner.id === void 0 ? null : String(owner.id));
}
function parseSchedule(value) {
	if (typeof value === "string") return {
		type: null,
		value: value.trim() || null
	};
	if (Array.isArray(value)) {
		const [first, second, third] = value;
		if (first === "crontab" && typeof second === "string") return {
			type: "crontab",
			value: second
		};
		if (first === "interval" && (typeof second === "number" || typeof second === "string") && typeof third === "string") return {
			type: "interval",
			value: `${second} ${third}`
		};
		if ((typeof first === "number" || typeof first === "string") && typeof second === "string") return {
			type: "interval",
			value: `${first} ${second}`
		};
		return {
			type: null,
			value: null
		};
	}
	if (!value || typeof value !== "object") return {
		type: null,
		value: null
	};
	const schedule = value;
	const type = typeof schedule.type === "string" ? schedule.type : null;
	if (type === "crontab" && typeof schedule.value === "string") return {
		type,
		value: schedule.value
	};
	if (type === "interval" && (typeof schedule.value === "number" || typeof schedule.value === "string") && typeof schedule.unit === "string") return {
		type,
		value: `${schedule.value} ${schedule.unit}`
	};
	return {
		type,
		value: null
	};
}
function getSchedule(monitor) {
	const config = monitor.config;
	if (!config) return null;
	const parsedSchedule = parseSchedule(config.schedule);
	const type = typeof config.schedule_type === "string" ? config.schedule_type : parsedSchedule.type;
	const value = parsedSchedule.value;
	const checkInMargin = typeof config.checkin_margin === "number" ? config.checkin_margin : null;
	const maxRuntime = typeof config.max_runtime === "number" ? config.max_runtime : null;
	if (type === null && value === null && checkInMargin === null && maxRuntime === null) return null;
	return {
		type,
		value,
		checkInMargin,
		maxRuntime
	};
}
var find_monitors_default = defineTool({
	name: "find_monitors",
	skills: ["inspect"],
	requiredScopes: ["org:read"],
	description: [
		"Find Sentry cron monitors.",
		"",
		"Use this tool when you need to:",
		"- List cron monitors in an organization",
		"- Find a monitor by name or slug before getting details",
		"- Check monitor status, owner, project, schedule, or recent environment state",
		"- When `hasMore` is true, narrow the results with project, environment, owner, or query filters",
		"",
		"<examples>",
		"find_monitors(organizationSlug='my-organization')",
		"find_monitors(organizationSlug='my-organization', projectSlug='backend', query='billing')",
		"</examples>"
	].join("\n"),
	inputSchema: {
		organizationSlug: ParamOrganizationSlug,
		regionUrl: ParamRegionUrl.nullable().default(null),
		projectSlug: ParamProjectSlugOrAll.nullable().default(null),
		environment: z.string().trim().describe("Optional environment name to limit monitor state.").nullable().default(null),
		owner: z.string().trim().describe("Optional owner filter, such as `user:123`, `team:456`, `myteams`, or `unassigned`.").nullable().default(null),
		query: z.string().trim().describe("Optional search query for monitor name or slug.").nullable().default(null),
		limit: z.number().int().positive().max(99).describe("Maximum number of monitors to return.").default(10)
	},
	annotations: {
		readOnlyHint: true,
		destructiveHint: false,
		openWorldHint: true
	},
	outputSchema: findMonitorsOutputSchema,
	async handler(params, context) {
		const apiService = apiServiceFromContext(context, { regionUrl: params.regionUrl ?? void 0 });
		const organizationSlug = params.organizationSlug;
		setTag("organization.slug", organizationSlug);
		const requestedProjectSlug = params.projectSlug && params.projectSlug !== "all" ? params.projectSlug : void 0;
		if (requestedProjectSlug) assertProjectRefWithinConstraint({
			resourceLabel: "Monitor list",
			scopedProjectSlug: context.constraints.projectSlug,
			project: { slug: requestedProjectSlug }
		});
		const projectSlug = context.constraints.projectSlug ?? requestedProjectSlug;
		if (projectSlug) setTag("project.slug", projectSlug);
		const monitors = await apiService.listMonitors({
			organizationSlug,
			projectSlug,
			environment: params.environment ?? void 0,
			owner: params.owner ?? void 0,
			query: params.query ?? void 0,
			limit: params.limit + 1
		});
		return structuredResult({
			monitors: monitors.slice(0, params.limit).map((monitor) => {
				const projectSlug$1 = monitor.project?.slug ?? monitor.project?.name ?? null;
				const environments = monitor.environments ?? [];
				return {
					id: monitor.id === void 0 ? null : String(monitor.id),
					slug: monitor.slug,
					name: monitor.name ?? monitor.slug,
					projectSlug: projectSlug$1,
					status: monitor.status ?? null,
					owner: getOwnerName(monitor),
					lastCheckIn: monitor.lastCheckIn ?? null,
					nextCheckIn: monitor.nextCheckIn ?? null,
					webUrl: apiService.getMonitorUrl(organizationSlug, monitor.slug, projectSlug$1 ?? void 0),
					schedule: getSchedule(monitor),
					environments: environments.slice(0, ENVIRONMENT_LIMIT).map((environment) => ({
						name: environment.name ?? null,
						status: environment.status ?? null,
						lastCheckIn: environment.lastCheckIn ?? null
					})),
					hasMoreEnvironments: environments.length > ENVIRONMENT_LIMIT
				};
			}),
			hasMore: monitors.length > params.limit
		});
	}
});

//#endregion
export { findMonitorsOutputSchema, find_monitors_default };
//# sourceMappingURL=find-monitors-DOaVdrCU.js.map